import { Injector } from '@angular/core';
import {
  UiZoneWidgetAttributeConfig,
  MaturityDecisionRequest,
  ActionButtonOutcomesType,
  SliderInputValueType,
  SavingsAccountInfoType,
  MaturityDecision,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import { Router } from '@angular/router';
import { catchError, take, tap } from 'rxjs';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { AccountSuccessCodes } from '@app/translations';
import { handleRedirection } from '../cta-redirection.utils';
import { FormID } from '@finxone-platform/form-action';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { UpdateFormDataActionWithId } from '../../../actions/form-submission.action';

type MaturityFormValues = {
  productCode: string;
  maturityDecisionAmount: string;
  sliderInputValueType: SliderInputValueType;
};

// Extract nested types from MaturityDecisionRequest type to keep them in sync
type MaturityAdvancedConfig = NonNullable<MaturityDecisionRequest['advanced']>;
type MaturityAction = MaturityAdvancedConfig['actions'][number];
type MoneyAmount = NonNullable<MaturityAction['amount']>;
type Strategy = MaturityAdvancedConfig['strategy'];

const STRATEGY: Strategy = 'CUSTOM';

export function submitMaturityDecisionPartialReinvest(
  injector: Injector,
  attri: UiZoneWidgetAttributeConfig,
): void {
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const activeAccount = store.selectSnapshot(SavingsAccountState.getCurrentAccount);

  if (!activeAccount) {
    alertHandlerService.showAlertFn('error', 'No active account found');
    return;
  }

  const formValues = extractMaturityFormValues(workflowFormsService);
  if (!formValues) {
    alertHandlerService.showAlertFn(
      'error',
      'Renewal product or reinvest/withdrawal amount not found, please try providing them again',
    );
    return;
  }

  const calculationMode = attri?.['calculationAppliesTo'] as MaturityDecision;
  const actions = buildMaturityActions(formValues, activeAccount, calculationMode);

  const payload: MaturityDecisionRequest = {
    advanced: {
      strategy: STRATEGY,
      actions,
    },
  };

  submitDecision(injector, payload, activeAccount.accountId, attri, ctaButtonSignalService);
}

function extractMaturityFormValues(workflowFormsService: WorkflowFormsService): MaturityFormValues | null {
  const allPageFormValues = workflowFormsService.getAllFormValuesFlattened();
  const formValues: MaturityFormValues = allPageFormValues[FormID.MATURITY_DECISION];

  if (!formValues?.productCode || !formValues?.maturityDecisionAmount) {
    return null;
  }

  return formValues;
}

function buildMaturityActions(
  formValues: MaturityFormValues,
  account: SavingsAccountInfoType,
  calculationMode?: MaturityDecision,
): MaturityAction[] {
  const isReinvestMode = calculationMode === MaturityDecision.RENEW;
  const isPercentage = formValues.sliderInputValueType === SliderInputValueType.PERCENTAGE;

  if (isReinvestMode) {
    return isPercentage
      ? calculateActionsFromReinvestPercentage(formValues, account)
      : calculateActionsFromReinvestAbsolute(formValues, account);
  }

  return isPercentage
    ? calculateActionsFromWithdrawalPercentage(formValues, account)
    : calculateActionsFromWithdrawalAbsolute(formValues, account);
}

function calculateActionsFromReinvestPercentage(
  formValues: MaturityFormValues,
  account: SavingsAccountInfoType,
): MaturityAction[] {
  const reinvestPercent = Number(formValues.maturityDecisionAmount);
  const balance = Number(account.balance);

  const reinvestAmount = (balance * reinvestPercent) / 100;
  const withdrawalAmount = balance - reinvestAmount;

  return buildActions({
    reinvestAmount,
    withdrawalAmount,
    currency: account.currency,
    productCode: formValues.productCode,
  });
}

function calculateActionsFromReinvestAbsolute(
  formValues: MaturityFormValues,
  account: SavingsAccountInfoType,
): MaturityAction[] {
  const reinvestAmount = Number(formValues.maturityDecisionAmount);
  const balance = Number(account.balance);

  const withdrawalAmount = balance - reinvestAmount;

  return buildActions({
    reinvestAmount,
    withdrawalAmount,
    currency: account.currency,
    productCode: formValues.productCode,
  });
}

function calculateActionsFromWithdrawalPercentage(
  formValues: MaturityFormValues,
  account: SavingsAccountInfoType,
): MaturityAction[] {
  const withdrawalPercent = Number(formValues.maturityDecisionAmount);
  const balance = Number(account.balance);

  const withdrawalAmount = (balance * withdrawalPercent) / 100;
  const reinvestAmount = balance - withdrawalAmount;

  return buildActions({
    reinvestAmount,
    withdrawalAmount,
    currency: account.currency,
    productCode: formValues.productCode,
  });
}

function calculateActionsFromWithdrawalAbsolute(
  formValues: MaturityFormValues,
  account: SavingsAccountInfoType,
): MaturityAction[] {
  let withdrawalAmount = Number(formValues.maturityDecisionAmount);
  const balance = Number(account.balance);

  if (withdrawalAmount >= balance) {
    withdrawalAmount = balance;
  }

  const reinvestAmount = balance - withdrawalAmount;

  return buildActions({
    reinvestAmount,
    withdrawalAmount,
    currency: account.currency,
    productCode: formValues.productCode,
  });
}

interface ActionBuildParams {
  reinvestAmount: number;
  withdrawalAmount: number;
  currency: string;
  productCode: string;
}

function buildActions(params: ActionBuildParams): MaturityAction[] {
  const actions: MaturityAction[] = [];

  if (params.reinvestAmount > 0) {
    actions.push(createReinvestAction(params.reinvestAmount, params.currency, params.productCode));
  }

  if (params.withdrawalAmount > 0) {
    actions.push(createWithdrawalAction(params.withdrawalAmount, params.currency));
  }

  return actions;
}

function createMoney(amount: number, currency: string): MoneyAmount {
  return {
    amount: amount.toString(),
    currency,
  };
}

function createReinvestAction(amount: number, currency: string, productCode: string): MaturityAction {
  return {
    type: 'REINVEST',
    amount: createMoney(amount, currency),
    productCode,
  };
}

function createWithdrawalAction(amount: number, currency: string): MaturityAction {
  return {
    type: 'WITHDRAWAL',
    amount: createMoney(amount, currency),
  };
}

function submitDecision(
  injector: Injector,
  payload: MaturityDecisionRequest,
  accountId: string,
  attributes: UiZoneWidgetAttributeConfig,
  ctaButtonSignalService: CtaButtonSignalService,
): void {
  const savingsAccountService = injector.get(SavingsAccountService);
  const store = injector.get(Store);
  const router = injector.get(Router);
  const alertHandlerService = injector.get(AlertHandlerService);
  const navConfig = attributes.widgetProperties?.actionNavigationConfigurations;
  const buttonAction = attributes?.['buttonActionType'];
  ctaButtonSignalService.setSignal({ [buttonAction]: false });

  savingsAccountService
    .submitMaturityDecision(payload, accountId)
    .pipe(
      tap(() => {
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          AccountSuccessCodes.MATURITY_DECISION_SUCCESS,
        ]);
        store.dispatch(new UpdateFormDataActionWithId(payload.advanced ?? {}, FormID.MATURITY_DECISION));
        if (navConfig?.success) {
          handleRedirection(attributes.widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);
        }
        ctaButtonSignalService.setSignal({ [buttonAction]: true });
      }),
      catchError((err) => {
        if (navConfig?.failure) {
          handleRedirection(attributes.widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
        }
        ctaButtonSignalService.setSignal({ [buttonAction]: true });
        throw err;
      }),
      take(1),
    )
    .subscribe();
}
